import { Cell, Pie, PieChart, ResponsiveContainer } from 'recharts';

export interface DonutLegendChartItem {
  name: string;
  value: number;
  color: string;
}

interface DonutLegendChartProps {
  title: string;
  centerValue: string | number;
  centerLabel: string;
  data: DonutLegendChartItem[];
  total: number;
  valueFormatter?: (item: DonutLegendChartItem) => string;
  showLegendPercent?: boolean;
  className?: string;
}

function getChartItemKey(item: DonutLegendChartItem, index: number) {
  return `${item.name}-${item.color}-${index}`;
}

export function DonutLegendChart({
  title,
  centerValue,
  centerLabel,
  data,
  total,
  valueFormatter,
  showLegendPercent = true,
  className = '',
}: DonutLegendChartProps) {
  const chartData = data.some((item) => item.value > 0)
    ? data
    : data.map((item, index) => ({ ...item, value: index === 0 ? 1 : 0 }));

  return (
    <div className={`w-full rounded-md border border-slate-100 bg-white p-3 shadow-sm ${className}`}>
      <h2 className="mb-3 text-sm font-medium text-slate-950">{title}</h2>
      <div className="grid max-w-full grid-cols-[110px_1fr] items-center gap-2">
        <div className="relative h-[130px]">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={chartData}
                cx="50%"
                cy="50%"
                innerRadius={35}
                outerRadius={45}
                startAngle={90}
                endAngle={450}
                paddingAngle={1}
                stroke="none"
                dataKey="value"
              >
                {chartData.map((entry, index) => (
                  <Cell key={getChartItemKey(entry, index)} fill={entry.color} />
                ))}
              </Pie>
            </PieChart>
          </ResponsiveContainer>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <p className="text-lg lg:text-xl font-medium leading-none text-slate-950">{centerValue}</p>
            <p className="text-xs text-slate-500">{centerLabel}</p>
          </div>
        </div>

        <div className="space-y-3">
          {data.map((item, index) => {
            const percent = total > 0 ? Math.round((item.value / total) * 1000) / 10 : 0;
            const value = valueFormatter ? valueFormatter(item) : String(item.value);
            return (
              <div
                key={getChartItemKey(item, index)}
                className="grid grid-cols-[max-content_max-content] items-center gap-3 text-xs"
              >
                <div className="flex min-w-0 items-center gap-2">
                  <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ backgroundColor: item.color }} />
                  <span className="truncate text-slate-600">{item.name}</span>
                </div>
                <span className="whitespace-nowrap font-medium text-slate-700 text-[10px] lg:text-xs">
                  {value}{showLegendPercent ? ` (${percent}%)` : ''}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
